import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { getCreatorProfileUrl, getCollectionUrlFromId } from '../../helpers/helpers';
import ShopmyImage from '../General/ShopmyImage';
import './CollectionAnalysisResult.scss';

const TIER_LABELS = { 1: 'Icon', 2: 'Trendsetter', 3: 'Ambassador', 4: 'Enthusiast' };
const FACET_GROUPS = [
  { key: 'items', label: 'Items' },
  { key: 'occasion', label: 'Occasion' },
  { key: 'aesthetic', label: 'Aesthetic' },
  { key: 'season', label: 'Season' },
  { key: 'brands', label: 'Brands' }
];

const CollectionAnalysisResult = props => {
  const { result, passingScore, saveAdjustment } = props;
  const [showReasoning, setShowReasoning] = useState(false);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scoreDraft, setScoreDraft] = useState('');
  const [reasonDraft, setReasonDraft] = useState('');
  const {
    id,
    Collection_id,
    source,
    score,
    coverImageScore,
    coverImageScoreReasoning,
    contentType,
    hardCap,
    adjustedScore,
    adjustmentReason,
    adjustedAt,
    adjustedByName,
    searchDescription,
    facets,
    reasoning,
    productCount,
    inStockCount,
    outOfStockCount,
    unknownStockCount,
    shoppability,
    freshness,
    lastActivityAt,
    collageUrl,
    scoredAt,
    collectionName,
    collectionImage,
    isPrivate,
    isArchived,
    creatorName,
    creatorUsername,
    creatorTier
  } = result;

  const isAdjusted = adjustedScore != null;
  const effectiveScore = isAdjusted ? adjustedScore : score;
  const isPassing = effectiveScore != null && effectiveScore >= passingScore;
  const image = collageUrl || collectionImage;
  const facetGroups = FACET_GROUPS.map(({ key, label }) => ({ key, label, values: facets?.[key]?.filter(Boolean) || [] })).filter(
    group => group.values.length
  );
  if (facets?.priceContext) facetGroups.push({ key: 'priceContext', label: 'Price', values: [facets.priceContext] });
  const collectionUrl = getCollectionUrlFromId(Collection_id);
  const creatorUrl = getCreatorProfileUrl(creatorUsername);

  const openAdjustForm = () => {
    setScoreDraft(effectiveScore != null ? String(Number(effectiveScore)) : '');
    setReasonDraft(adjustmentReason || '');
    setIsAdjusting(true);
  };

  const submitAdjustment = async () => {
    const newScore = Number(scoreDraft);
    if (!scoreDraft || Number.isNaN(newScore) || newScore < 1 || newScore > 10) return window.ALERT.error('Score must be between 1 and 10.');
    if (!reasonDraft.trim()) return window.ALERT.error('Please give a rationale for the adjustment.');

    setIsSaving(true);
    try {
      await saveAdjustment(id, { adjustedScore: newScore, adjustmentReason: reasonDraft.trim() });
      setIsAdjusting(false);
    } catch {
      window.ALERT.error('Could not save the score adjustment, please try again.');
    }
    setIsSaving(false);
  };

  const removeAdjustment = async () => {
    setIsSaving(true);
    try {
      await saveAdjustment(id, { adjustedScore: null });
      setIsAdjusting(false);
    } catch {
      window.ALERT.error('Could not remove the score adjustment, please try again.');
    }
    setIsSaving(false);
  };

  // In-stock % is over catalog-checked pins only — pins without a catalog match are "unknown"
  // rather than counted against the collection.
  const stockCheckedCount = inStockCount != null ? inStockCount + outOfStockCount : null;
  const metaStats = [
    {
      label: 'cover',
      display: coverImageScore != null ? `${coverImageScore}/100` : '—',
      title: coverImageScoreReasoning || 'Current cover has not been assessed. Scores of 70+ are hero candidates; crop safety is separate.'
    },
    { label: 'products', display: productCount != null ? productCount : '—' },
    {
      label: 'in stock',
      display: stockCheckedCount ? `${Math.round((inStockCount / stockCheckedCount) * 100)}%` : '—',
      title: stockCheckedCount
        ? `${inStockCount} in stock · ${outOfStockCount} out of stock · ${unknownStockCount} unknown · ranking multiplier ${Number(shoppability).toFixed(2)}`
        : 'No catalog-matched pins checked yet'
    },
    {
      label: 'freshness',
      display: freshness != null ? Number(freshness).toFixed(2) : '—',
      title: lastActivityAt ? `Last activity (collection edit or pin change) ${moment(lastActivityAt).fromNow()}` : null
    },
    { label: 'scored', display: scoredAt ? moment(scoredAt).format('MMM D') : '—' }
  ];

  return (
    <div className='collection-analysis-result-outer'>
      <div className='result-image'>
        {image ? <ShopmyImage source={image} alt={collectionName} /> : <div className='image-placeholder' />}
      </div>

      <div className='result-main'>
        <div className='result-title-row'>
          <a className='result-name' href={collectionUrl} target='_blank' rel='noopener noreferrer'>
            {collectionName || `Collection #${Collection_id}`}
          </a>
          {source === 'concierge' && <span className='result-flag neutral'>Concierge</span>}
          {!!isPrivate && <span className='result-flag'>Private</span>}
          {!!isArchived && <span className='result-flag'>Archived</span>}
          {contentType === 'singleOutfit' && <span className='result-flag'>Single Outfit</span>}
          {hardCap && <span className='result-flag'>Capped: {hardCap}</span>}
        </div>
        <div className='result-creator'>
          <a href={creatorUrl} target='_blank' rel='noopener noreferrer'>
            {creatorName || creatorUsername}
          </a>
          {TIER_LABELS[creatorTier] && <span className={cn('tier-badge', `tier-${creatorTier}`)}>{TIER_LABELS[creatorTier]}</span>}
        </div>
        {searchDescription && <div className='result-description'>{searchDescription}</div>}
        {!!facetGroups.length && (
          <div className='result-facets'>
            {facetGroups.map(({ key, label, values }) => (
              <div key={key} className='facet-group'>
                <span className='facet-label'>{label}</span>
                {values.map(value => (
                  <span key={value} className='facet-chip'>
                    {value}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
        {reasoning && (
          <div className='result-reasoning'>
            <div className='reasoning-toggle' onClick={() => setShowReasoning(!showReasoning)}>
              {showReasoning ? 'Hide rationale' : 'Show rationale'}
            </div>
            {showReasoning && <div className='reasoning-text'>{reasoning}</div>}
          </div>
        )}
        {isAdjusted && !isAdjusting && (
          <div className='result-adjustment'>
            <div className='adjustment-summary'>
              Adjusted from {score != null ? Number(score).toFixed(1) : '—'} to {Number(adjustedScore).toFixed(1)}
              {adjustedByName ? ` by ${adjustedByName}` : ''}
              {adjustedAt ? ` · ${moment(adjustedAt).format('MMM D')}` : ''}
            </div>
            {adjustmentReason && <div className='adjustment-reason'>{adjustmentReason}</div>}
          </div>
        )}
        {isAdjusting && (
          <div className='result-adjust-form'>
            {hardCap && Number(scoreDraft) >= passingScore && (
              <div className='adjust-cap-warning'>
                The grader hard-capped this collection ({hardCap}). Saving a passing score overrides the cap and lets it surface in
                search.
              </div>
            )}
            <div className='adjust-form-row'>
              <input
                type='number'
                min='1'
                max='10'
                step='0.5'
                value={scoreDraft}
                onChange={({ target }) => setScoreDraft(target.value)}
                placeholder='Score'
              />
              <textarea
                rows={2}
                value={reasonDraft}
                onChange={({ target }) => setReasonDraft(target.value)}
                placeholder='Why are you adjusting this score?'
              />
            </div>
            <div className='adjust-form-actions'>
              <button type='button' className='primary' disabled={isSaving} onClick={submitAdjustment}>
                {isSaving ? 'Saving...' : 'Save adjustment'}
              </button>
              {isAdjusted && (
                <button type='button' disabled={isSaving} onClick={removeAdjustment}>
                  Restore LLM score
                </button>
              )}
              <button type='button' disabled={isSaving} onClick={() => setIsAdjusting(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='result-meta'>
        {metaStats.map(({ label, display, title }) => (
          <div key={label} className='meta-stat' title={title || undefined}>
            <span className='value'>{display}</span>
            <span className='label'>{label}</span>
          </div>
        ))}
      </div>

      <div className='result-score-column'>
        <div className={cn('result-score', { passing: isPassing, unscored: effectiveScore == null, adjusted: isAdjusted })}>
          <div className='score-value'>{effectiveScore != null ? Number(effectiveScore).toFixed(1) : '—'}</div>
          <div className='score-label'>
            {effectiveScore == null ? 'unscored' : isAdjusted ? 'adjusted' : isPassing ? 'passing' : 'below gate'}
          </div>
        </div>
        <div className='adjust-toggle' onClick={() => (isAdjusting ? setIsAdjusting(false) : openAdjustForm())}>
          {isAdjusting ? 'Cancel' : isAdjusted ? 'Edit adjustment' : 'Adjust score'}
        </div>
      </div>
    </div>
  );
};

CollectionAnalysisResult.propTypes = {
  result: PropTypes.object.isRequired,
  passingScore: PropTypes.number.isRequired,
  saveAdjustment: PropTypes.func.isRequired
};

export default CollectionAnalysisResult;
